import React, { useRef } from "react"
import {
  SearchBox,
  Highlight,
  useInstantSearch,
  useHits,
} from "react-instantsearch"

import {
  Link,
  useI18next,
  useTranslation,
} from "@zestia/gatsby-plugin-react-i18next"
import { gtmTracking } from "@/utils/gtm-tracking"

import {
  AUTOCOMPLETE_MIN_CHARS,
  AUTOCOMPLETE_SEARCH_DELAY_MS,
} from "@/utils/typesense-config"

export const SupportSearchAutoCompleteResults = ({ indexName }) => {
  const { t } = useTranslation()

  const { indexUiState: state } = useInstantSearch()
  const { results } = useHits()

  const hasMoreResults = results.nbHits > results.hitsPerPage

  const queryLength = state.query?.length ?? 0

  return (
    <div hidden={!state.query}>
      <div className="support-search-box__auto-complete-results">
        {results.hits.length === 0 && queryLength > AUTOCOMPLETE_MIN_CHARS ? (
          <div className="support-search-box__auto-complete-results--no-results">
            {t("support.search.no-results")}
          </div>
        ) : (
          <ol className="support-search-box__auto-complete-results-list">
            {results.hits.map(hit => {
              return (
                <li
                  key={hit.id}
                  className="support-search-box__auto-complete-result p-5"
                >
                  <h2 className="h5 support-search-box__auto-complete-result-title">
                    <a
                      href={hit.url}
                      onClick={() =>
                        gtmTracking(
                          "Support Search Result Click",
                          hit.title,
                          "",
                        )()
                      }
                    >
                      <Highlight
                        attribute="title"
                        hit={hit}
                        highlightedTagName="em"
                      />
                    </a>
                  </h2>

                  <p className="support-search-box__auto-complete-result-snippet">
                    <Highlight
                      attribute="body"
                      hit={hit}
                      highlightedTagName="em"
                    />
                  </p>
                </li>
              )
            })}

            {hasMoreResults && (
              <li className="p-5">
                <h2 className="h5 support-search-box__auto-complete-result-title">
                  <Link
                    to={`/support/search?${indexName}[query]=${state.query}`}
                    onClick={() =>
                      gtmTracking("Support Search Click", "More Results", "")()
                    }
                  >
                    {t("support.search.more-results")}
                  </Link>
                </h2>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  )
}

export const SupportSearchBox = ({
  indexName,
  searchAsYouType = false,
  navigateOnSubmit = true,
  children,
}) => {
  const { t } = useTranslation()
  const { navigate } = useI18next()

  const queryRef = useRef("")

  let activeDebounceTimer = undefined

  return (
    <div className="support-search-box">
      <SearchBox
        classNames={{
          form: "support-search-box__form",
          input: "support-search-box__input",
          submit: "support-search-box__submit",
          reset: "support-search-box__reset",
        }}
        searchAsYouType={searchAsYouType}
        onSubmit={() => {
          if (navigateOnSubmit) {
            navigate(`/support/search?${indexName}[query]=${queryRef.current}`)
          }
        }}
        placeholder={t("support.search.placeholder")}
        submitIconComponent={() => t("support.search.search")}
        queryHook={(query, search) => {
          queryRef.current = query

          if (activeDebounceTimer) {
            clearTimeout(activeDebounceTimer)
          }

          activeDebounceTimer = setTimeout(
            () => search(query),
            AUTOCOMPLETE_SEARCH_DELAY_MS,
          )
        }}
      />

      {children}
    </div>
  )
}
